import { API_BASE_URL } from './constants';
import type { UserClassInterface } from '../types/userClass.interface';
import { authenticatedFetch } from '@beholos/holoscommon';
import { trackEvent } from './analytics.service';

// Helper to build query params from filters
const buildQueryString = (filters: Record<string, any>) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    // For booleans or primitives, just append as string
    if (typeof value === 'boolean' || typeof value === 'number' || typeof value === 'string') {
      params.append(key, String(value));
    } else if (value instanceof Date) {
      params.append(key, value.toISOString());
    }
  });
  return params.toString();
};

interface RawUserClass {
  _id: string;
  user: string;
  class: any;
  video?: any;
  userVideo?: any;
  date: string;
  startTime: string;
  endTime: string;
  cancelled: boolean;
  attended: boolean;
  points?: number;
  userClassHash?: { classHash: string };
}

interface UserClassesResponse {
  userClasses: RawUserClass[];
  userClassesAttendedCount: number;
  userClassesPastCount: number;
  userClassesUpcomingCount: number;
  userVideos: any[];
}

export const fetchUserClasses = async (filters: {
  past?: boolean;
  future?: boolean;
  start?: Date;
  end?: Date;
  page?: number;
  limit?: number;
}): Promise<{ classes: UserClassInterface[]; metadata: Omit<UserClassesResponse, 'userClasses' | 'userVideos'> }> => {
  try {
    const query = buildQueryString(filters);

    const response = await authenticatedFetch(`${API_BASE_URL}/user-classes${query ? `?${query}` : ''}`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: UserClassesResponse = await response.json();


    const rawList: RawUserClass[] = Array.isArray(data) ? data : data.userClasses;

    const classes = rawList.map((uc): UserClassInterface => ({
      ...uc,
      _id: uc._id,
      user: uc.user,
      attended: uc.attended,
      cancelled: uc.cancelled,
      startTime: uc.startTime,
      endTime: uc.endTime,
      class: uc.class,
      video: uc.video,
      userVideo: uc.userVideo,
      points: uc.points,
      userClassHash: uc.userClassHash,
    }));

    const metadata = {
      userClassesAttendedCount: data.userClassesAttendedCount || 0,
      userClassesPastCount: data.userClassesPastCount || 0,
      userClassesUpcomingCount: data.userClassesUpcomingCount || 0,
    };

    return { classes, metadata };
  } catch (error) {
    console.error('Error fetching user classes:', error);
    return {
      classes: [],
      metadata: {
        userClassesAttendedCount: 0,
        userClassesPastCount: 0,
        userClassesUpcomingCount: 0,
      }
    };
  }
};

// Cancel a user class
export const cancelUserClass = async (userClassId: string): Promise<{ status: number, message: string }> => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/user-class/cancel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        userClassId: userClassId
      })
    });

    const data = await response.json();

    if (!data) {
      console.error('No response data received when cancelling class');
      return { status: 500, message: 'Algo salió mal, escríbenos a contacto@beholos.com.' };
    }
    
    if (!response.ok) {
      console.error('Failed to cancel class. Status:', data.status, 'Response:', data.message);
    } else {
      trackEvent('class_cancellation', { user_class_id: userClassId });
    }

    console.log('Class cancelled successfully');
    return data;
  } catch (error) {
    console.error('Error cancelling class:', error);
    return { status: 500, message: 'Algo salió mal, escríbenos a contacto@beholos.com.' };
  }
};